import React from 'react';
import * as Sift from '@sift/types';
import * as Types from '../typings';
import * as Entity from 'pages/Pages/typings';
import _get from 'lodash/get';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';
import useTerminology from '@sift/skipper/helpers/useTerminology';

import AttributeTypeCard from 'pages/AttributesNew/AttributeTypeCard';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import TextField from '@material-ui/core/TextField';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Tooltip from '@material-ui/core/Tooltip';

import makeStyles from '@material-ui/core/styles/makeStyles';

interface Props {
  attribute?: Sift.Field;
  item?: {
    description: string;
    Icon: any;
    link: string;
    title: string;
  };
  values: Types.Client;
  onChange: (field: string, value: any) => void;
  entityType?: Entity.EntityType;
}

function AttributeBasicInfo({ attribute, item, values, onChange, entityType }: Props) {
  const classes = useStyles();
  const terminology = useTerminology();
  const { objectKey } = useParams<{ objectKey: string }>();
  const parentFields = _get(entityType, '_fields.reachOutToMeFor.fields', []);
  const isGeneralField = objectKey === 'reachOutToMeFor.general';
  const isGeneralAndNoCategorizedAttributes =
    isGeneralField && parentFields.includes(objectKey) && parentFields.length === 1;
  const cardRootClass = classnames(classes.cardRoot, {
    [classes.disabled]: isGeneralAndNoCategorizedAttributes,
  });
  const shouldRenderMessageOptions = _get(attribute, 'baseField') === 'messageAddress';
  const shouldRenderDateFormat = false;
  const shouldRenderLinkName = _get(attribute, 'baseField') === 'hyperlink';

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.displayName', e.target.value);
  };
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.display.description', e.target.value);
  };

  const handleDateFormatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.dateFormat', e.target.value);
  };

  const handleButtonTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === 'msteams' || e.target.value === 'slack') {
      onChange('client.display.messengerType', e.target.value);
    } else {
      onChange('client.display.messengerType', undefined);
    }
  };

  const handleButtonTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.display.buttonText', e.target.value);
  };

  const handleLinkNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('client.display.linkTitle', e.target.value);
  };

  const cardContent = (
    <Card className={cardRootClass}>
      <CardContent>
        <div className={classes.marginBottom}>
          <Typography variant="h6">Attribute Name</Typography>
          <TextField
            id="attribute name"
            fullWidth={true}
            disabled={isGeneralField}
            value={_get(values, 'displayName', '')}
            onChange={handleNameChange}
            inputProps={{ 'aria-label': 'attribute name', maxLength: 50 }}
          />
        </div>
        <div className={classes.marginBottom}>
          <Typography variant="h6">Description</Typography>
          <Typography variant="subtitle2">
            {terminology.plural} will see this description when prompted to edit the attribute.
          </Typography>
          <TextField
            fullWidth={true}
            id="attribute-description"
            value={_get(values, 'display.description', '')}
            multiline={true}
            onChange={handleDescriptionChange}
            inputProps={{ 'aria-label': 'attribute description' }}
          />
        </div>
        {shouldRenderDateFormat && (
          <div>
            <Typography variant="h6">Date Format</Typography>
            <RadioGroup
              aria-label="date format"
              name="date-format"
              value={_get(values, 'dateFormat', 'MM/dd/yyyy')}
              onChange={handleDateFormatChange}
            >
              <FormControlLabel value="MM/dd/yyyy" control={<Radio />} label="Month, Day, Year" />
              <FormControlLabel value="MM/dd" control={<Radio />} label="Month, Day" />
            </RadioGroup>
          </div>
        )}
        {shouldRenderLinkName && (
          <div>
            <Typography variant="h6">Link Title</Typography>
            <Typography variant="subtitle2">
              If provided, the link will be displayed as this text rather than the full hyperlink
              entered.
            </Typography>
            <TextField
              id="link-title"
              value={_get(values, 'display.linkTitle', '')}
              multiline={true}
              onChange={handleLinkNameChange}
              inputProps={{ 'aria-label': 'link title' }}
            />
          </div>
        )}
        {shouldRenderMessageOptions && (
          <>
            <div className={classes.marginBottom}>
              <Typography variant="h6">Button Style</Typography>
              <Typography variant="subtitle2">
                If you are specifically linking to a certain messenger, you may use its icon.
              </Typography>
              <RadioGroup
                aria-label="button style"
                name="button-style"
                value={_get(values, 'display.messengerType', 'generic')}
                onChange={handleButtonTypeChange}
              >
                <FormControlLabel value="generic" control={<Radio />} label="Generic" />
                <FormControlLabel value="msteams" control={<Radio />} label="Microsoft Teams" />
                <FormControlLabel value="slack" control={<Radio />} label="Slack" />
              </RadioGroup>
            </div>
            <div>
              <Typography variant="h6">Button Text</Typography>
              <Typography variant="subtitle2">
                Provide the text to be displayed on the button. The default is "Message".
              </Typography>
              <TextField
                id="button-text"
                value={_get(values, 'display.buttonText', '')}
                onChange={handleButtonTextChange}
                placeholder="Message"
                inputProps={{ 'aria-label': 'button text' }}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className={classes.root}>
      {isGeneralAndNoCategorizedAttributes ? (
        <Tooltip
          arrow={true}
          id="tooltip-id"
          placement="right"
          title='When categorized sub-attributes are disabled, this attribute&apos;s description will not be shown to users. You can still edit the description of "Reach Out To Me For".'
        >
          {cardContent}
        </Tooltip>
      ) : (
        cardContent
      )}
      {item && <AttributeTypeCard className={classes.marginLeft} item={item} link={false} />}
    </div>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  cardRoot: {
    flexGrow: 1,
    maxWidth: 900,
  },
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
  marginLeft: {
    marginLeft: theme.spacing(3),
  },
  disabled: {
    opacity: theme.palette.opacity.thirtyEightPercent,
  },
}));

export default AttributeBasicInfo;
